import type { ReactNode } from 'react';
import { cn } from 'helpers/cn';
import { GlowHalo, LogoBars } from './LogoBars';

type AiButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  /** A request this button started is out: the glow runs round it and the bars bounce. */
  busy?: boolean;
  className?: string;
  ariaLabel?: string;
};

/**
 * Every button that starts AI work, everywhere in the product: the editor's top bar AI button,
 * "Ink" (a dark gray fill, white text and the bars in their on-dark tints, ai-chat.css), so a
 * reader can tell at a glance which actions spend AI credits. While its request runs it takes the
 * working glow. Navigating to something AI wrote is not AI work, and uses an ordinary button.
 */
export const AiButton = ({
  children,
  onClick,
  type = 'button',
  disabled = false,
  busy = false,
  className,
  ariaLabel,
}: AiButtonProps) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || busy}
    aria-busy={busy || undefined}
    aria-label={ariaLabel}
    className={cn(
      'ai-trigger ai-ink text-meta font-semibold',
      busy && 'ai-glow is-working',
      'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
      'disabled:cursor-not-allowed disabled:opacity-60',
      className,
    )}
  >
    {busy && <GlowHalo />}
    <LogoBars size={12} state={busy ? 'thinking' : 'idle'} className="on-dark" />
    {children}
  </button>
);
